import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DownloadIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';
import {
  getOfficePreviewUrl,
  extractFileName,
  getFileTypeLabel,
} from './document-utils';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: '900px',
    width: '100%',
  },
  dialogContent: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '500px',
    backgroundColor: '#f5f5f5',
  },
  iframe: {
    width: '100%',
    height: '600px',
    border: 'none',
    borderRadius: theme.spacing(0.5),
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
  },
  errorMessage: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.error.main,
  },
}));

interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  documentUrl: string;
  mimeType?: string;
  fileName?: string;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  onClose,
  documentUrl,
  mimeType = '',
  fileName = 'Document',
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [previewError, setPreviewError] = useState(false);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = extractFileName(fileName);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNew = () => {
    window.open(documentUrl, '_blank');
  };

  const isPdf = mimeType.includes('pdf');
  const isOfficeDoc = mimeType.includes('word') ||
    mimeType.includes('sheet') ||
    mimeType.includes('presentation') ||
    mimeType.includes('wordprocessingml') ||
    mimeType.includes('spreadsheetml') ||
    mimeType.includes('presentationml');

  const previewUrl = isOfficeDoc ? getOfficePreviewUrl(documentUrl) : documentUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Box className={classes.headerActions}>
          <Typography variant="h6" className={classes.title}>
            {extractFileName(fileName)} ({getFileTypeLabel(mimeType)})
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {isLoading && !previewError && (
          <Box className={classes.loader}>
            <CircularProgress />
            <Typography variant="caption">
              Loading {getFileTypeLabel(mimeType)}...
            </Typography>
          </Box>
        )}
        {previewError && (
          <Box className={classes.errorMessage}>
            <Typography variant="body2">
              Preview not available for this file type.
            </Typography>
            <Typography variant="caption">
              Click "Open in New Window" or "Download" to view the file.
            </Typography>
          </Box>
        )}
        {!previewError && (
          <iframe
            src={previewUrl}
            title={extractFileName(fileName)}
            className={classes.iframe}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setPreviewError(true);
            }}
            style={{
              display: isLoading || previewError ? 'none' : 'block',
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenInNew}
          color="primary"
        >
          Open in New Window
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          color="primary"
        >
          Download
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
